import { ReactElement, useEffect, useMemo } from 'react';
import { Tabs, TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';

import { Page } from '@ohpen/enso-ui';
import {
  ProcurationFeesTab,
  ProcurationFeeRulesTab,
  PROCURATION_FEES_VALUE_LIST_NAMES,
  getProcurationFeeOptions,
  ProcurationFeeCalculator,
} from '@pmp/components';

import { usePmpFetch, useListTabParam } from '@pmp/hooks';
import { useValueListsStore } from '@pmp/stores';

export const ProcurationFeesPage = (): ReactElement => {
  const [isFetching, , procurationFees, , fetchProcurationFees] = usePmpFetch('procurationFeesGetAllProcurationFees');
  const { valueLists, getValueLists } = useValueListsStore();
  const { t } = useTranslation();

  useEffect(() => {
    getValueLists({ fetchIfMissing: PROCURATION_FEES_VALUE_LIST_NAMES });
  }, [getValueLists]);

  useEffect(() => {
    fetchProcurationFees();
  }, [fetchProcurationFees]);

  const tabItems: TabsProps['items'] = useMemo(() => {
    const feeRulesValueLists = { ...valueLists, PROCURATION_FEES: getProcurationFeeOptions(procurationFees) };
    const items = [
      {
        key: 'fees',
        label: t('lender.procurationFees.labels.feesList'),
        children: (
          <ProcurationFeesTab valueLists={valueLists} data={procurationFees} isFetching={isFetching} fetchData={fetchProcurationFees} />
        ),
      },
      {
        key: 'rules',
        label: t('lender.procurationFees.labels.feeRules'),
        children: <ProcurationFeeRulesTab valueLists={feeRulesValueLists} />,
      },
      {
        key: 'calculator',
        label: t('lender.procurationFees.labels.feeCalculator'),
        children: <ProcurationFeeCalculator />,
      },
    ];

    return items;
  }, [valueLists, procurationFees, t, isFetching, fetchProcurationFees]);

  const tabKeys = useMemo(() => (tabItems ?? []).map(item => String(item.key)), [tabItems]);
  const tabParam = useListTabParam(tabKeys);

  return (
    <Page>
      <Page.Title>{t('lender.procurationFees.title')}</Page.Title>
      <Page.Content fill>
        <div className="enso-ui-tabs-wrapper">
          <Tabs {...tabParam} items={tabItems} />
        </div>
      </Page.Content>
    </Page>
  );
};
